import React from 'react';
import { Grid } from '@mentimeter/slides-presentation-ui/grid';
import { MasonryGrid } from '@mentimeter/slides-presentation-ui/masonry-grid';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Heading } from '@mentimeter/ragnar-ui/typography';
import { useTranslations } from 'next-intl';
import { Response } from '../../Response';
import { getVotingOnAnswersSplitResults } from '../../utils/getVotingOnAnswersSplitResults';
import { ResponseWithUpvotes } from '../../ResponseWithUpvotes';
import { ModerationButton } from '../../ui/ModerationButton';
import { FadeIn } from '../../grouping/motion/FadeIn';
import { NUM_COLUMNS_WITHOUT_IMAGE_LAYOUT } from '../../utils/constants';
import type { OpenEndedResponse } from '../../types';

interface ResponsesGridProps {
  responses: OpenEndedResponse[];
  shouldSplitResponses: boolean;
  numberOfColumns?: number;
  onItemClick: (index: number) => void;
  shouldAnimate: boolean;
  isReadonly: boolean;
}

export const ResponsesGrid = ({
  responses,
  shouldSplitResponses,
  numberOfColumns = NUM_COLUMNS_WITHOUT_IMAGE_LAYOUT,
  onItemClick,
  shouldAnimate,
  isReadonly,
}: ResponsesGridProps) => {
  const translate = useTranslations('presenting.legacy');

  if (!shouldSplitResponses) {
    return (
      <MasonryGrid numberOfColumns={numberOfColumns}>
        {(columnIndex) =>
          responses.map(
            (item, index) =>
              index % numberOfColumns === columnIndex && (
                <Response
                  index={index}
                  key={item.id}
                  responseText={item.responseText}
                  participantDisplayName={item.participantDisplayName}
                  onClick={onItemClick}
                  controlsOnHover={
                    <ModerationButton selectedAnswer={item.responseText} />
                  }
                  shouldAnimate={shouldAnimate}
                  isReadonly={isReadonly}
                />
              ),
          )
        }
      </MasonryGrid>
    );
  }

  const {
    resultsWithVotes,
    resultsWithoutVotes,
    indexOfFirstResultWithoutVotes,
  } = getVotingOnAnswersSplitResults(responses);

  const maxVoteCount = Math.max(...resultsWithVotes.map((v) => v.voteCount));
  const hasResultsWithVotes = resultsWithVotes.length > 0;

  return (
    <>
      {hasResultsWithVotes && (
        <Box
          as="section"
          data-testid="responses--voted"
          flexDirection="column"
          alignContent="flex-start"
          flexWrap="wrap"
          width="100%"
          mb={5}
        >
          <Grid numberOfColumns={numberOfColumns} shouldAnimate>
            {resultsWithVotes.map((item, index) => (
              <ResponseWithUpvotes
                key={item.id}
                index={index}
                responseText={item.responseText}
                participantDisplayName={item.participantDisplayName}
                upvotes={item.voteCount}
                onClick={onItemClick}
                isTopVoted={item.voteCount === maxVoteCount}
                controlsOnHover={
                  <ModerationButton selectedAnswer={item.responseText} />
                }
                isReadonly={isReadonly}
              />
            ))}
          </Grid>
        </Box>
      )}

      {resultsWithoutVotes.length > 0 && (
        <FadeIn delay={hasResultsWithVotes ? 700 : 0} width="100%">
          <Box
            as="section"
            data-testid="responses--unvoted"
            flexDirection="column"
            alignContent="flex-start"
            flexWrap="wrap"
            width="100%"
          >
            {hasResultsWithVotes && (
              <Heading
                fontSize={4}
                fontWeight="regular"
                fontFamily="heading"
                mb={3}
                color="textWeaker"
                width="100%"
              >
                {translate('open.responses_without_votes')}
              </Heading>
            )}

            <MasonryGrid numberOfColumns={numberOfColumns}>
              {(columnIndex) =>
                resultsWithoutVotes.map(
                  (item, index) =>
                    index % numberOfColumns === columnIndex && (
                      <Response
                        index={index + indexOfFirstResultWithoutVotes} // Index of elements should continue from the previous grid, important for the onClick handler
                        key={item.id}
                        responseText={item.responseText}
                        participantDisplayName={item.participantDisplayName}
                        onClick={onItemClick}
                        controlsOnHover={
                          <ModerationButton
                            selectedAnswer={item.responseText}
                          />
                        }
                        shouldAnimate={shouldAnimate}
                        isReadonly={isReadonly}
                      />
                    ),
                )
              }
            </MasonryGrid>
          </Box>
        </FadeIn>
      )}
    </>
  );
};
